import React from 'react';
import { createRoot } from 'react-dom/client';
import './scss/styles.scss';
import App from './App';
import * as serviceWorker from './serviceWorker';
import { theme } from '~/theme';
import { ThemeProvider } from '@mui/material';
import { ConfigProvider } from 'antd';
import { queryClient } from '~/reactQueryClient';
import { QueryClientProvider } from 'react-query';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/ja';
import ja_JP from 'antd/es/locale/ja_JP';
import updateLocale from 'dayjs/plugin/updateLocale';

dayjs.extend(updateLocale);

dayjs.locale('ja');
dayjs.updateLocale('ja', {
  weekStart: 1,
});

// Suppress Chrome extension errors that are harmless
// These errors occur when browser extensions try to communicate but the port closes,
// or when extensions fail to access certain browser APIs (like file saving locations)
if (typeof window !== 'undefined') {
  const originalError = console.error;
  const originalWarn = console.warn;

  // Helper function to check if error should be suppressed
  const shouldSuppressError = (message: string): boolean => {
    const lowerMessage = message.toLowerCase();
    return (
      lowerMessage.includes('runtime.lasterror') ||
      lowerMessage.includes('message port closed') ||
      lowerMessage.includes('could not get default saving location') ||
      lowerMessage.includes('background.js') ||
      lowerMessage.includes('extension context invalidated')
    );
  };

  console.error = (...args: any[]) => {
    const errorMessage = args[0]?.toString() || '';
    // Suppress Chrome extension errors
    if (shouldSuppressError(errorMessage)) {
      return;
    }
    originalError.apply(console, args);
  };

  console.warn = (...args: any[]) => {
    const warnMessage = args[0]?.toString() || '';
    // Suppress Chrome extension warnings
    if (shouldSuppressError(warnMessage)) {
      return;
    }
    originalWarn.apply(console, args);
  };

  // Also handle unhandled promise rejections that might contain this error
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason?.message || event.reason?.toString() || '';

    // Handle chunk loading errors (common after deployment)
    if (
      event.reason?.name === 'ChunkLoadError' ||
      reason.includes('Loading chunk') ||
      reason.includes('Failed to fetch dynamically imported module')
    ) {
      console.warn(
        'Chunk load error detected, reloading page...',
        event.reason,
      );
      event.preventDefault();
      // Reload the page to get the new chunks
      setTimeout(() => {
        window.location.reload();
      }, 100);
      return;
    }

    if (shouldSuppressError(reason)) {
      event.preventDefault();
    }
  });
}

const container = document.getElementById('root');
const root = createRoot(container!);

root.render(
  <QueryClientProvider client={queryClient}>
    <ConfigProvider theme={{ cssVar: true }} locale={ja_JP}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <ThemeProvider theme={theme}>
          <App />
        </ThemeProvider>
      </LocalizationProvider>
    </ConfigProvider>
  </QueryClientProvider>,
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
